import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { isEmpty, omitBy, isNil, isNull } from "lodash";
import { useHistory, useLocation } from "react-router-dom";
import { routeToBoxesView } from "Helpers/index";
import useSession from "Hooks/session.hook";
import { Row, Col } from "antd";
import orderStatusesMap from "Data/order-statuses.json";
import DataTableFilter from "Components/shared/data-table-filter";
import { DataTable } from "@life-line-lab/components-react";
import ErrorBoundary from "antd/lib/alert/ErrorBoundary";
import { toCapitalisedSpaced } from "Helpers/to-capitalised-spaced";
import { getStatusStyle } from "Helpers/get-status-colours";

export interface CompletedBoxesListProps {
  parcelList: any;
  onFilter?: any;
  sortingHubOptions: any;
  initialStatus: any;
}

const CompletedBoxesList: React.FC<CompletedBoxesListProps> = ({
  parcelList,
  onFilter,
  sortingHubOptions,
  initialStatus,
}) => {
  const { roles } = useSession();
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const initialFilter: any = {
    status: "",
    sortingHub: "",
  };
  const [filterState, setFilterState] = useState<{
    [key: string]: string | boolean;
  }>(initialFilter);

  useEffect(() => {
    if (onFilter && !isEmpty(filterState)) {
      const filterPayload = omitBy(
        filterState,
        (input) => isNil(input) || isEmpty(input),
      );
      onFilter(filterPayload);
    }
  }, [filterState]);

  const initialFilterSelection = [
    {
      key: "status",
      name: "status",
      label: t("placeholder.byStatus"),
      placeholder: t("placeholder.byStatus"),
      value: filterState?.status,
      options: [{ label: t("option.all"), value: "" }].concat(
        initialStatus.map((option) => {
          return {
            label: toCapitalisedSpaced(option),
            value: option,
          };
        }),
      ),
    },
    {
      key: "sortingHub",
      name: "sortingHub",
      label: t("label.originHub"),
      placeholder: t("label.originHub"),
      value: filterState?.sortingHub,
      options: [{ label: t("option.all"), value: "" }].concat(
        sortingHubOptions?.map((hub) => {
          return {
            label: hub.locationName || "",
            value: hub._id,
          };
        }),
      ),
    },
  ];

  const [filterSelection, setFilterSelection] = useState<any>(
    initialFilterSelection,
  );

  useEffect(() => {
    setFilterSelection(initialFilterSelection);
  }, [sortingHubOptions]);

  const handleOpenBoxesTrackingView = (boxID) => {
    routeToBoxesView(boxID, location, history);
  };

  const handleSearch = async (text: string) => {
    setFilterState((prevFilter) => ({ ...prevFilter, orFields: text }));
  };

  const handleFilterSelection = (selection) => {
    setFilterState((prevFilter) => ({
      ...prevFilter,
      sortingHub: selection.sortingHub,
      type: selection.type,
      status: selection.status ? selection.status : initialStatus,
      handoverPoint: selection.destination,
    }));
  };

  // Box ID is actually Box Code in database
  const columns: any = [
    {
      title: t("label.originHub").toLocaleUpperCase(),
      dataIndex: "sortingHub",
      width: 150,
      ellipsis: true,
      render: (_: any, record: any) => {
        return (
          <div className="align-flex-end ">
            <span
              className="half-square custom-icon mb-5"
              style={{
                borderLeft: `12px solid transparent`,
                borderBottom: `12px solid #E8A51A`,
              }}
            >
              &nbsp;
            </span>
            <span>&nbsp;{record.sortingHubData?.name || ""}</span>
          </div>
        );
      },
    },
    {
      title: t("label.boxID").toLocaleUpperCase(),
      dataIndex: "boxCode",
      width: 150,
      ellipsis: true,
      render: (_: any, record: any) => {
        return <a className="clickable-id">{record.boxCode || ""}</a>;
      },
      onCell: (record, rowIndex) => {
        return {
          onClick: (e: React.MouseEvent<HTMLElement>) => {
            handleOpenBoxesTrackingView(record._id);
          },
        };
      },
    },
    {
      title: t("label.status").toLocaleUpperCase(),
      dataIndex: "status",
      width: 100,
      ellipsis: true,
      render: (_: any, record: any) => {
        const status = orderStatusesMap?.[record.status]?.[roles[0]["type"]];

        return (
          <span className="status-indicator" style={getStatusStyle(status)}>
            {status ? toCapitalisedSpaced(status) : ""}
          </span>
        );
      },
    },
    {
      title: t("label.time").toLocaleUpperCase(),
      dataIndex: "updatedAt",
      width: 105,
      ellipsis: true,
      render: (_: any, record: any) => {
        return record.updatedAt ? (
          <span>
            {record.updatedAt?.substr(0, record.updatedAt.indexOf("T"))}
          </span>
        ) : (
          <span>&#11835;</span>
        );
      },
    },
    {
      title: t("label.driverName").toLocaleUpperCase(),
      dataIndex: ["assignedDriverData"],
      width: 105,
      ellipsis: true,
      render: (_: any, record: any) => {
        return (
          <span>
            {!isNull(record.assignedDriverData)
              ? record.assignedDriverData.fullName
              : record.assignedDriver || "Unassigned"}
          </span>
        );
      },
    },
    {
      title: t("label.handoverPoint").toLocaleUpperCase(),
      dataIndex: ["handoverPoint"],
      width: 105,
      ellipsis: true,
      render: (_: any, record: any) => {
        return <span>{record?.handoverPointData?.name}</span>;
      },
    },
  ];

  return (
    <ErrorBoundary>
      <div className="parcel-order-list status-selector">
        <DataTable
          rowKey={(record: any) => {
            return record._id;
          }}
          // rowSelection={rowSelection}
          columns={columns}
          scroll={{ x: true }}
          dataSource={parcelList || []}
          title={() => (
            <Row>
              <Col span={16} className="filter-component">
                <DataTableFilter
                  searchPlaceholder={t("placeholder.completedSearchbar")}
                  onSearch={(text: string) => {
                    handleSearch(text);
                  }}
                  onFilter={(selection) => handleFilterSelection(selection)}
                  renderFilterSelection={filterSelection}
                />
              </Col>
            </Row>
          )}
        />
      </div>
    </ErrorBoundary>
  );
};

export default CompletedBoxesList;
